/**
 * Parsing for the newsletter link stored on `user_profiles.substack_url`.
 *
 * Nothing validated this column. Both profile editors wrote raw text to it and
 * Reader's Corner built `https://${value}` at click time, so an empty string
 * produced `https://` — unparseable, and assigning it to location.href throws
 * `SyntaxError: Invalid URL`. An address like `name@gmail.com` was worse: it
 * parses fine, as credentials against host gmail.com, and quietly sent the
 * reader somewhere unrelated.
 *
 * Every read and write of the column goes through here now.
 *
 * Substack and Medium identify a publication differently, so they can't share
 * one rule: a Substack publication IS the host (a pasted post URL still means
 * the publication, so the path is dropped), while on medium.com the handle is
 * the first path segment and must be kept.
 */

function build(platform, host, path, feed) {
  return {
    platform,
    stored: `${host}${path}`, // canonical column value — protocol-less, as today
    href: `https://${host}${path}`, // where "Read →" goes
    feed, // the RSS endpoint
  };
}

/**
 * Normalises free text into a newsletter address, or returns null if it can't
 * be one. Accepts a bare host (`foo.substack.com`), a full URL, and a Medium
 * path (`medium.com/@you`), with or without protocol, www, or trailing slash.
 */
export function parseNewsletterUrl(raw) {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;

  let url;
  try {
    url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null; // includes the empty string, which parses to `https://`
  }

  // Credentials mean the host isn't what the user thinks they typed:
  // `name@gmail.com` parses as user `name` against host `gmail.com`.
  if (url.username || url.password) return null;

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const segments = url.pathname.split('/').filter(Boolean);

  // Requires a subdomain — bare substack.com is their homepage, not a writer's.
  if (/\.substack\.com$/.test(host)) {
    return build('Substack', host, '', `https://${host}/feed`);
  }

  // medium.com/@handle and medium.com/publication both identify a feed.
  if (host === 'medium.com') {
    const handle = segments[0];
    if (!handle) return null;
    return build('Medium', host, `/${handle}`, `https://medium.com/feed/${handle}`);
  }

  // Legacy per-publication subdomains, e.g. towardsdatascience.medium.com.
  if (/\.medium\.com$/.test(host)) {
    return build('Medium', host, '', `https://${host}/feed`);
  }

  return null;
}

/** True when `raw` is an address we can actually open. */
export function isNewsletterUrl(raw) {
  return parseNewsletterUrl(raw) !== null;
}

/**
 * Returns `raw` if it is a parseable http(s) URL, else null.
 *
 * Post links come back from an RSS feed we don't control, so they get the same
 * treatment as user input before anything navigates to them.
 */
export function safeHttpUrl(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}
